import PageForbidden from "../PageForbidden/PageForbidden";
import PageNotFound from "../PageNotFound/PageNotFound";
import ForbiddenErrorResponse from "@app/config/api/responses/ForbiddenErrorResponse";
import NotFoundErrorResponse from "@app/config/api/responses/NotFoundErrorResponse";
import environment from "@app/config/environment";
import Icon from "@lib/common/components/Icon/Icon";
import { Box, Button, Center, Group, Stack, Text, Title } from "@mantine/core";
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import type { FallbackProps } from "react-error-boundary";

const isProd = environment.PROD;

export default function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const message = error instanceof Error ? error.message : "An unexpected error occurred.";

  useEffect(() => {
    if (error) {
      Sentry.captureException(error);
    }
  }, [error]);

  if (error instanceof NotFoundErrorResponse) {
    return <PageNotFound />;
  }

  if (error instanceof ForbiddenErrorResponse) {
    return <PageForbidden />;
  }

  return (
    <Box pos="absolute" inset={0}>
      <Center mt="30vh">
        <Stack gap="xl">
          <Stack gap="md">
            <Group align="center">
              {!isProd && <Icon name="circle-exclamation" size="1.8rem" color="red" variant="solid" />}
              <Title order={2}>{isProd ? "Sorry, something went wrong" : message}</Title>
            </Group>
            {!isProd && <Text>See the console for error details.</Text>}
            {isProd && (
              <>
                <Text>
                  We are sorry for the inconvenience. This is not expected behavior, neither is it your fault.
                </Text>
                <Text>
                  The Atinary engineering team has already been notified and will look into the problem immediately.
                </Text>
              </>
            )}
          </Stack>
          <Group gap="xs">
            <Button variant="light" onClick={() => history.back()} maw="200">
              Go back
            </Button>
            <Button variant="light" onClick={resetErrorBoundary} maw="200">
              Try again
            </Button>
          </Group>
        </Stack>
      </Center>
    </Box>
  );
}
